import {SellerType} from '@sollapay/enums';
import {useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {buildRosterSidebarItem} from '@/components/roster';

import {countIssuesByRow} from '../utils';

import type {SellersFormValues} from '../utils';
import type {RosterSidebarItem} from '@/components/roster';
import type {FieldArrayWithId, UseFormReturn} from 'react-hook-form';

export const SELLER_ROSTER_PREFIX = 'activation.packages.trustDetails.sellerDetails.roster';
const TYPE_PREFIX = 'activation.packages.trustDetails.form.options.sellerTypes';
const SELLER_TYPE_KEYS: Record<SellerType, string> = {
  [SellerType.CORPORATION]: 'corporation',
  [SellerType.INDIVIDUAL]: 'individual'
};

interface UseSellerSidebarItemsParams {
  form: UseFormReturn<SellersFormValues>;
  sellerFields: FieldArrayWithId<SellersFormValues, 'sellers'>[];
  fieldErrors: Record<string, string | undefined>;
  showErrors: boolean;
  isExtracting: (index: number) => boolean;
}

/** Sidebar cards for the sellers roster — name, seller type, missing-field count once errors are shown and an AI badge while that seller's documents are read. */
export function useSellerSidebarItems({
  form,
  sellerFields,
  fieldErrors,
  showErrors,
  isExtracting
}: UseSellerSidebarItemsParams): RosterSidebarItem[] {
  const {t} = useTranslation('trustAccounts');
  const {t: tCommon} = useTranslation('common');
  const sellers = useWatch({control: form.control, name: 'sellers'});

  const issuesByRow = countIssuesByRow(fieldErrors);

  return sellerFields.map((field, index) => {
    const seller = sellers[index];
    const typeKey = seller?.sellerType && SELLER_TYPE_KEYS[seller.sellerType];

    return buildRosterSidebarItem({
      id: field.id,
      name: seller?.fullName,
      fallbackName: t(`${SELLER_ROSTER_PREFIX}.newSeller`),
      typeLabel: typeKey ? t(`${TYPE_PREFIX}.${typeKey}`) : undefined,
      issues: showErrors && seller ? (issuesByRow.get(index) ?? 0) : undefined,
      issuesLabel: count => t(`${SELLER_ROSTER_PREFIX}.issuesCount`, {count}),
      completeLabel: t(`${SELLER_ROSTER_PREFIX}.complete`),
      extractingLabel: isExtracting(index) ? tCommon('aiExtraction.field.generating') : undefined
    });
  });
}
